import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { DocumentRecord } from '../../models/Document'
import { User } from '../../models/User'

import { RootState } from '../store'
import { DocumentType } from '../../models/DocumentType'
import { FileInfo } from '../media/file'
import { type SyncStatus } from '../../apps/professional-app/fa/MeetingSummary/ExternalUpdates/models'

export interface DocumentFileInfo {
  fileId: string
  fileName: string
  fileSize: number
  contentType: string
}

interface DocumentSummaryResult {
  summary: string
}

interface DocumentState {
  isLoading: boolean
  pollingEnabled: boolean
  document: DocumentRecord | null
  creator: User | null
  creatorIsLoading: boolean
  selectedDocumentType: DocumentType | null
  fileInfo: FileInfo | DocumentFileInfo | null
  fileInfoIsLoading: boolean
  summaryResultIsLoading: boolean
  summaryPollingEnabled: boolean
  summaryResult: DocumentSummaryResult | null
  summaryEditorContent: string | null
  summarySyncStatus: SyncStatus[]
  summaryExecuteIsLoading: boolean
  summaryInteractionIsLoading: boolean
  summaryAiAgentId: string | null
  summaryIsEditing: boolean
  isNotRelatedToClient: boolean
}

const initialState: DocumentState = {
  isLoading: true,
  pollingEnabled: false,
  document: null,
  creator: null,
  creatorIsLoading: true,
  selectedDocumentType: null,
  fileInfo: null,
  fileInfoIsLoading: false,
  summaryResultIsLoading: true,
  summaryPollingEnabled: false,
  summaryResult: null,
  summaryEditorContent: null,
  summarySyncStatus: [],
  summaryExecuteIsLoading: false,
  summaryInteractionIsLoading: false,
  summaryAiAgentId: null,
  summaryIsEditing: false,
  isNotRelatedToClient: false,
}

export const documentSlice = createSlice({
  name: 'document',
  initialState,
  reducers: {
    setDocumentLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setDocument: (state, action: PayloadAction<DocumentRecord>) => {
      state.document = action.payload
    },
    startDocumentPolling: (state) => {
      state.pollingEnabled = true
    },
    stopDocumentPolling: (state) => {
      state.pollingEnabled = false
    },
    setDocumentCreator: (state, action: PayloadAction<User>) => {
      state.creator = action.payload
    },
    setDocumentCreatorIsLoading: (state, action: PayloadAction<boolean>) => {
      state.creatorIsLoading = action.payload
    },
    setSelectedDocumentType: (state, action: PayloadAction<DocumentType>) => {
      state.selectedDocumentType = action.payload
    },
    setDocumentFileInfo: (state, action: PayloadAction<FileInfo | DocumentFileInfo>) => {
      state.fileInfo = action.payload
    },
    setDocumentFileInfoIsLoading: (state, action: PayloadAction<boolean>) => {
      state.fileInfoIsLoading = action.payload
    },
    setDocumentSummaryResultLoading: (state, action: PayloadAction<boolean>) => {
      state.summaryResultIsLoading = action.payload
    },
    startDocumentSummaryPolling: (state) => {
      state.summaryPollingEnabled = true
    },
    stopDocumentSummaryPolling: (state) => {
      state.summaryPollingEnabled = false
    },
    setDocumentSummaryResult: (state, action: PayloadAction<DocumentSummaryResult>) => {
      state.summaryResult = { ...state.summaryResult, ...action.payload }
      state.summaryEditorContent = state.summaryResult.summary
    },
    clearDocumentSummaryResult: (state) => {
      state.summaryResult = null
      state.summaryEditorContent = null
    },
    setDocumentSummaryEditorContent: (state, action: PayloadAction<string>) => {
      state.summaryEditorContent = action.payload
    },
    resetDocumentSummaryEditorContent: (state) => {
      state.summaryEditorContent = state.summaryResult ? state.summaryResult.summary : null
    },
    setDocumentIsNotRelatedToClient: (state, action: PayloadAction<boolean>) => {
      state.isNotRelatedToClient = action.payload
    },
    setDocumentSummarySyncStatus: (state, action: PayloadAction<SyncStatus[]>) => {
      state.summarySyncStatus = action.payload
    },
    setDocumentSummaryExecutionLoading: (state, action: PayloadAction<boolean>) => {
      state.summaryExecuteIsLoading = action.payload
    },
    setDocumentSummaryInteractionLoading: (state, action: PayloadAction<boolean>) => {
      state.summaryInteractionIsLoading = action.payload
    },
    setDocumentSummaryAiAgentId: (state, action: PayloadAction<string>) => {
      state.summaryAiAgentId = action.payload
    },
    setDocumentSummaryEditing: (state, action: PayloadAction<boolean>) => {
      state.summaryIsEditing = action.payload
    },
    resetDocument: () => initialState,
  },
})

export const {
  setDocumentLoading,
  setDocument,
  startDocumentPolling,
  stopDocumentPolling,
  setDocumentCreator,
  setDocumentCreatorIsLoading,
  setSelectedDocumentType,
  setDocumentFileInfo,
  setDocumentFileInfoIsLoading,
  setDocumentSummaryResultLoading,
  startDocumentSummaryPolling,
  stopDocumentSummaryPolling,
  setDocumentSummaryResult,
  clearDocumentSummaryResult,
  setDocumentSummaryEditorContent,
  resetDocumentSummaryEditorContent,
  setDocumentIsNotRelatedToClient,
  setDocumentSummarySyncStatus,
  setDocumentSummaryExecutionLoading,
  setDocumentSummaryInteractionLoading,
  setDocumentSummaryAiAgentId,
  setDocumentSummaryEditing,
  resetDocument,
} = documentSlice.actions

export const selectDocument = (state: RootState) => state.processDocument.document.document

export const selectDocumentLoading = (state: RootState) => state.processDocument.document.isLoading

export const selectDocumentPollingEnabled = (state: RootState) => state.processDocument.document.pollingEnabled

export const selectDocumentCreator = (state: RootState) => state.processDocument.document.creator

export const selectDocumentCreatorIsLoading = (state: RootState) => state.processDocument.document.creatorIsLoading

export const selectSelectedDocumentType = (state: RootState) => state.processDocument.document.selectedDocumentType

export const selectDocumentFileInfo = (state: RootState) => state.processDocument.document.fileInfo

export const selectDocumentFileInfoIsLoading = (state: RootState) => state.processDocument.document.fileInfoIsLoading

export const selectDocumentSummaryResultLoading = (state: RootState) => state.processDocument.document.summaryResultIsLoading

export const selectDocumentSummaryPollingEnabled = (state: RootState) => state.processDocument.document.summaryPollingEnabled

export const selectDocumentSummaryResult = (state: RootState) => state.processDocument.document.summaryResult

export const selectDocumentSummaryEditorContent = (state: RootState) => state.processDocument.document.summaryEditorContent

export const selectDocumentIsNotRelatedToClient = (state: RootState) => state.processDocument.document.isNotRelatedToClient

export const selectDocumentSummaryExecutionLoading = (state: RootState) => state.processDocument.document.summaryExecuteIsLoading

export const selectDocumentSummaryInteractionLoading = (state: RootState) => state.processDocument.document.summaryInteractionIsLoading

export const selectDocumentSummaryAiAgentId = (state: RootState) => state.processDocument.document.summaryAiAgentId

export const selectDocumentSummaryEditing = (state: RootState) => state.processDocument.document.summaryIsEditing

export const selectDocumentSummarySyncStatus = (state: RootState) => state.processDocument.document.summarySyncStatus
